import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { isNil, sortBy } from 'lodash'
import queryString from 'query-string'

import { PageHeader } from '@/components/headers/PageHeader'
import { Card } from '@/components/cards/Card'
import Select from '@/components/forms/Select'
import { FormField } from '@/components/forms/FormField'
import { FilterForm } from '@/components/filters/FilterForm'
import { getCoassembleClients, getCourses } from '@/reducers/baltoAcademy/baltoAcademy.actions'
import { fetchOrganizations } from '@/reducers/organizations/organizations.actions'

import { categorizeCourses } from './BaltoAcademy.helpers'
import { CourseTrackingsTable } from './CourseTrackingsTable'

import './BaltoAcademyReportingPage.scss'

const BaltoAcademyReportingPage = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const organizations = useSelector((state) => state.organizations)
  const { id: currentOrgId, parent_id: parentOrgId } = useSelector(
    (state) => state.currentOrganization
  )
  const isBaltoAdmin = currentOrgId === 1

  const [clients, setClients] = useState([])
  // Preselect the org when returning from a user page (?org=<clientIdentifier>).
  const [selectedClientId, setSelectedClientId] = useState(
    queryString.parse(location.search).org ?? null
  )
  const [courses, setCourses] = useState([])
  const [selectedCourseId, setSelectedCourseId] = useState(null)

  const clientId = isBaltoAdmin ? selectedClientId : currentOrgId ?? null

  useEffect(() => {
    dispatch(fetchOrganizations())
    if (isBaltoAdmin) {
      dispatch(getCoassembleClients()).then((data) => {
        setClients(data)
      })
    }
  }, [isBaltoAdmin])

  const orgNameById = useMemo(
    () => new Map(organizations.map((org) => [String(org.id), org.name])),
    [organizations]
  )

  const orgOptions = useMemo(
    () =>
      sortBy(
        clients.map((client) => ({
          value: client.clientIdentifier,
          label: orgNameById.get(String(client.clientIdentifier)) || client.clientIdentifier,
        })),
        (option) => String(option.label).toLowerCase()
      ),
    [clients, orgNameById]
  )

  useEffect(() => {
    setSelectedCourseId(null)
    if (isNil(clientId)) {
      setCourses([])
      return undefined
    }
    let cancelled = false
    dispatch(getCourses(clientId)).then((data) => {
      if (!cancelled) {
        setCourses(Array.isArray(data) ? data : [])
      }
    })
    return () => {
      cancelled = true
    }
  }, [clientId])

  const courseOptions = useMemo(
    () =>
      Object.entries(categorizeCourses(courses, parentOrgId)).map(
        ([category, categoryCourses]) => ({
          label: category,
          options: categoryCourses.map((course) => ({
            value: course.id,
            label: course.displayTitle,
          })),
        })
      ),
    [courses, parentOrgId]
  )

  return (
    <div data-testid="academy-reporting-page">
      <PageHeader title="Reporting" />

      <FilterForm small sticky={false}>
        {isBaltoAdmin && (
          <FormField>
            <label>Organization</label>
            <div data-testid="org-dropdown">
              <Select
                options={orgOptions}
                value={selectedClientId}
                onChange={(option) => setSelectedClientId(option?.value || null)}
                placeholder="Select Organization"
                fixedWidth
              />
            </div>
          </FormField>
        )}

        <FormField>
          <label>Course</label>
          <div data-testid="course-dropdown">
            <Select
              options={courseOptions}
              value={selectedCourseId}
              onChange={(option) => setSelectedCourseId(option?.value || null)}
              placeholder="Select Course"
              isDisabled={isNil(clientId)}
              fixedWidth
            />
          </div>
        </FormField>
      </FilterForm>

      <Card className="no-padding">
        <CourseTrackingsTable courseId={selectedCourseId} clientId={clientId} />
      </Card>
    </div>
  )
}

export default BaltoAcademyReportingPage
